import React, { useEffect, useState } from "react";
import {
  X,
  User,
  Mail,
  Ticket,
  MapPin,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Users,
  Briefcase,
  Globe,
  FileText,
} from "lucide-react";

const formatDob = (value) => {
  if (!value) return "N/A";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 8) {
    const day = Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4));
    const date = new Date(year, month - 1, day);
    if (!Number.isNaN(date.getTime()) && date.getDate() === day) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return String(value);
};

const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "N/A";
  const num = Number(amount);
  const formatted = Number.isNaN(num)
    ? String(amount)
    : num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  return currency ? `${currency} ${formatted}` : formatted;
};

const getTicket = (registration) => {
  if (registration?.ticket_type && typeof registration.ticket_type === "object") {
    return registration.ticket_type;
  }
  return {
    name: registration?.ticket_type || "N/A",
    price: registration?.amount_paid ?? registration?.total_amount,
    currency: registration?.currency,
  };
};

const isGroupRegistration = (registration) =>
  registration?.registration_type === "group" ||
  (Array.isArray(registration?.attendees) && registration.attendees.length > 0);

const displayName = (registration) => {
  if (isGroupRegistration(registration)) {
    return (
      registration.group_info?.organization_name ||
      registration.group_info?.primary_contact_name ||
      "Group registration"
    );
  }
  const name = [registration?.first_name, registration?.last_name]
    .filter(Boolean)
    .join(" ");
  return name || registration?.name || "Attendee";
};

const initials = (name) =>
  (name || "U")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

const normalizeAnswers = (answers) => {
  if (!answers) return [];
  let list = answers;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter((item) => item && (item.question || item.answer));
};

const isPlaceholderAnswer = (answer) =>
  typeof answer === "string" && answer.trim().startsWith("N/A (group registration");

const groupAnswers = (answers) => {
  const sections = [];
  const bySection = new Map();
  normalizeAnswers(answers).forEach((item) => {
    if (isPlaceholderAnswer(item.answer)) return;
    const section = item.section || "Other";
    if (!bySection.has(section)) {
      const entry = { section, items: [] };
      bySection.set(section, entry);
      sections.push(entry);
    }
    bySection.get(section).items.push(item);
  });
  return sections;
};

const isImageAnswer = (item) =>
  item?.type === "signature" ||
  (typeof item?.answer === "string" && item.answer.startsWith("data:image"));

const getPaymentStatusBadge = (status) => {
  const statusLower = (status || "").toLowerCase();
  switch (statusLower) {
    case "paid":
    case "completed":
      return { label: "Paid", color: "bg-green-100 text-green-700", icon: CheckCircle };
    case "pending":
      return { label: "Pending", color: "bg-orange-100 text-orange-700", icon: Clock };
    case "failed":
      return { label: "Failed", color: "bg-red-100 text-red-700", icon: XCircle };
    case "refunded":
      return { label: "Refunded", color: "bg-gray-100 text-gray-700", icon: XCircle };
    default:
      return {
        label: status || "Unknown",
        color: "bg-gray-100 text-gray-700",
        icon: Clock,
      };
  }
};

const Field = ({ label, value, className = "" }) => (
  <div className={className}>
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-sm font-semibold text-gray-900 break-words">{value || "N/A"}</p>
  </div>
);

const Section = ({ icon: Icon, title, children, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-50 border-gray-200",
    purple: "bg-purple-50 border-purple-200",
    blue: "bg-blue-50 border-blue-200",
  };
  return (
    <section className={`rounded-xl border p-4 ${tones[tone] || tones.gray}`}>
      <h4 className="mb-4 flex items-center text-sm font-bold uppercase tracking-wide text-gray-900">
        {Icon && <Icon className="mr-2 h-4 w-4 text-purple-600" />}
        {title}
      </h4>
      {children}
    </section>
  );
};

const Photo = ({ src, alt, className = "" }) => {
  if (!src) return null;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <img
        src={src}
        alt={alt}
        className={`rounded-lg border border-gray-200 bg-white object-cover ${className}`}
      />
    </a>
  );
};

const AnswerList = ({ answers }) => {
  const sections = groupAnswers(answers);
  if (sections.length === 0) {
    return <p className="text-sm text-gray-500">No motivational answers recorded.</p>;
  }

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.section} className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-purple-700">
            {section.section}
          </p>
          <div className="space-y-3">
            {section.items.map((item, index) => (
              <div key={`${item.question}-${index}`}>
                <p className="text-xs font-medium text-gray-500">{item.question}</p>
                {isImageAnswer(item) ? (
                  <img
                    src={item.answer}
                    alt="Signature"
                    className="mt-2 max-h-28 w-full rounded-lg border border-gray-200 bg-white object-contain"
                  />
                ) : (
                  <p className="mt-1 text-sm font-semibold text-gray-900 whitespace-pre-wrap">
                    {item.answer === null || item.answer === undefined || String(item.answer).trim() === ""
                      ? "Not answered"
                      : item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const PersonDetails = ({ person }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Nationality" value={person.nationality} />
      <Field label="Gender" value={person.gender} />
      <Field label="Date of birth" value={person.date_of_birth ? formatDob(person.date_of_birth) : "N/A"} />
      <Field label="Profession" value={person.profession} />
      <Field label="Organization" value={person.organization} />
      <Field label="Country of residence" value={person.country_of_residence} />
      <Field label="Departure city" value={person.departure_city} />
      <Field label="Contact number" value={person.contact_number || person.phone} />
      <Field label="Email" value={person.email} className="sm:col-span-2" />
      <Field label="Address" value={person.address} className="sm:col-span-2" />
    </div>
    {person.id_card_front && (
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">ID card</p>
        <Photo src={person.id_card_front} alt="ID card" className="h-36 w-full max-w-xs" />
      </div>
    )}
  </div>
);

/**
 * View attendee details in a right-side drawer.
 * Supports single registrations and group registrations.
 */
const ViewAttendeeModal = ({ isOpen, onClose, attendee }) => {
  const [activeAttendeeIndex, setActiveAttendeeIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    setActiveAttendeeIndex(0);
  }, [attendee?.slug, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !attendee) return null;

  const group = isGroupRegistration(attendee);
  const name = displayName(attendee);
  const ticket = getTicket(attendee);
  const paymentStatus = getPaymentStatusBadge(attendee.payment_status);
  const StatusIcon = paymentStatus.icon;
  const groupInfo = attendee.group_info || {};
  const primaryContact = attendee.primary_contact_details || {};
  const people = group ? attendee.attendees || [] : [];
  const activePerson = people[activeAttendeeIndex] || people[0];
  const accountUser = attendee.user || {};
  const profilePhoto = group
    ? primaryContact.profile_photo
    : attendee.profile_photo;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/50"
        onClick={onClose}
      />

      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl">
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-purple-100 bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 text-white">
          <div className="flex min-w-0 items-center gap-4">
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt=""
                className="h-14 w-14 flex-shrink-0 rounded-xl object-cover ring-2 ring-white/40"
              />
            ) : (
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-white/20 text-lg font-bold">
                {initials(name)}
              </div>
            )}
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold capitalize">
                  {group ? "Group" : attendee.registration_type || "Single"}
                </span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${paymentStatus.color}`}>
                  <StatusIcon className="mr-1 h-3 w-3" />
                  {paymentStatus.label}
                </span>
              </div>
              <h3 className="truncate text-xl font-bold">{name}</h3>
              <p className="mt-1 truncate text-sm text-purple-100">
                {group
                  ? `${groupInfo.number_of_attendees || people.length} attendee${(groupInfo.number_of_attendees || people.length) === 1 ? "" : "s"}`
                  : attendee.email || "Attendee details"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-white hover:bg-white/15"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-6">
          <Section icon={Ticket} title="Ticket" tone="purple">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Ticket type" value={ticket.name} />
              <Field label="Price" value={formatMoney(ticket.price, ticket.currency)} />
              <Field label="Payment status" value={paymentStatus.label} />
              <Field label="Agent code" value={attendee.agent_code} />
              {ticket.description && (
                <Field label="Description" value={ticket.description} className="sm:col-span-2" />
              )}
            </div>
            {Array.isArray(ticket.whats_included) && ticket.whats_included.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Included</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {ticket.whats_included.map((item) => (
                    <span
                      key={item}
                      className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-200"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {group ? (
            <>
              <Section icon={Building2} title="Group">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Organization" value={groupInfo.organization_name} />
                  <Field label="Number of attendees" value={groupInfo.number_of_attendees || people.length} />
                  <Field label="Country" value={groupInfo.country} />
                  <Field label="City" value={groupInfo.city} />
                </div>
              </Section>

              <Section icon={User} title="Primary contact" tone="blue">
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Photo
                    src={primaryContact.profile_photo}
                    alt={groupInfo.primary_contact_name || "Primary contact"}
                    className="h-24 w-24"
                  />
                  <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Name" value={groupInfo.primary_contact_name || primaryContact.primary_contact_name} />
                    <Field label="Phone" value={groupInfo.primary_contact_phone || primaryContact.primary_contact_phone} />
                    <Field
                      label="Email"
                      value={groupInfo.primary_contact_email || primaryContact.primary_contact_email}
                      className="sm:col-span-2"
                    />
                  </div>
                </div>
                {groupInfo.primary_contact_signature && (
                  <div className="mt-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Signature</p>
                    <Photo
                      src={groupInfo.primary_contact_signature}
                      alt="Primary contact signature"
                      className="mt-2 h-28 w-full max-w-sm object-contain"
                    />
                  </div>
                )}
              </Section>

              <Section icon={Users} title="Attendees">
                {people.length === 0 ? (
                  <p className="text-sm text-gray-500">No attendees were included in this registration.</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {people.map((person, index) => {
                        const personName = [person.first_name, person.last_name].filter(Boolean).join(" ") || `Attendee ${index + 1}`;
                        const selected = index === activeAttendeeIndex;
                        return (
                          <button
                            key={`${person.email || personName}-${index}`}
                            type="button"
                            onClick={() => setActiveAttendeeIndex(index)}
                            className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                              selected
                                ? "bg-purple-600 text-white"
                                : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-purple-50"
                            }`}
                          >
                            {personName}
                          </button>
                        );
                      })}
                    </div>
                    {activePerson && (
                      <div className="rounded-xl border border-gray-200 bg-white p-4">
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-sm font-bold text-purple-700">
                            {initials([activePerson.first_name, activePerson.last_name].filter(Boolean).join(" "))}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {[activePerson.first_name, activePerson.last_name].filter(Boolean).join(" ") || "Attendee"}
                            </p>
                            <p className="text-xs text-gray-500">{activePerson.email || "No email"}</p>
                          </div>
                        </div>
                        <PersonDetails person={activePerson} />
                      </div>
                    )}
                  </div>
                )}
              </Section>
            </>
          ) : (
            <>
              <Section icon={User} title="Personal information">
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Photo src={attendee.profile_photo} alt={name} className="h-28 w-28" />
                  <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Full name" value={name} />
                    <Field label="Gender" value={attendee.gender} />
                    <Field label="Date of birth" value={formatDob(attendee.date_of_birth)} />
                    <Field label="Nationality" value={attendee.nationality} />
                    <Field label="Profession" value={attendee.profession} />
                    <Field label="Organization" value={attendee.organization} />
                  </div>
                </div>
              </Section>

              <Section icon={Mail} title="Contact">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Email" value={attendee.email} />
                  <Field label="Phone" value={attendee.contact_number || attendee.phone} />
                </div>
              </Section>

              <Section icon={MapPin} title="Travel">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Country of residence" value={attendee.country_of_residence} />
                  <Field label="Departure city" value={attendee.departure_city} />
                  <Field label="Address" value={attendee.address} className="sm:col-span-2" />
                </div>
              </Section>

              <Section icon={FileText} title="Motivational answers">
                <AnswerList
                  answers={attendee.motivation_answers || attendee.motivational_answers}
                />
              </Section>
            </>
          )}

          {group && (
            <Section icon={FileText} title="Motivational answers">
              {people.length === 0 ? (
                <AnswerList
                  answers={
                    attendee.motivation_answers ||
                    attendee.motivational_answers ||
                    primaryContact.motivation_answers
                  }
                />
              ) : (
                <div className="space-y-5">
                  {people.map((person, index) => {
                    const personName =
                      [person.first_name, person.last_name].filter(Boolean).join(" ") ||
                      `Attendee ${index + 1}`;
                    return (
                      <div key={`${person.email || personName}-${index}`}>
                        <p className="mb-3 text-sm font-bold text-gray-900">{personName}</p>
                        <AnswerList
                          answers={person.motivation_answers || person.motivational_answers}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          )}

          {(accountUser.first_name || accountUser.email || accountUser.card_number) && (
            <Section icon={Briefcase} title="Registered account">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Name"
                  value={[accountUser.first_name, accountUser.last_name].filter(Boolean).join(" ")}
                />
                <Field label="Email" value={accountUser.email} />
                <Field label="Phone" value={accountUser.phone_number} />
                <Field label="Location" value={[accountUser.location, accountUser.region, accountUser.country].filter(Boolean).join(", ")} />
                {accountUser.document_type && (
                  <Field label="Document" value={String(accountUser.document_type).replace(/_/g, " ")} />
                )}
                {accountUser.card_number && <Field label="Document number" value={accountUser.card_number} />}
              </div>
              {(accountUser.id_card_front || accountUser.id_card_back) && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Photo src={accountUser.id_card_front} alt="ID card front" className="h-36 w-full" />
                  <Photo src={accountUser.id_card_back} alt="ID card back" className="h-36 w-full" />
                </div>
              )}
            </Section>
          )}

          <Section icon={Globe} title="Registration">
            <Field label="Registration ID" value={attendee.slug} />
          </Section>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </aside>
    </>
  );
};

export default ViewAttendeeModal;
